import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'query-panel-recent-queries';
const MAX_RECENT_QUERIES = 12;

function persistToStorage(queries: string[]) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(queries));
    } catch (error) {
        console.error('Failed to save recent queries to localStorage', error);
    }
}

function loadFromStorage(): string[] {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
    } catch (error) {
        console.error('Failed to load recent queries from localStorage', error);
        return [];
    }
}

export function useRecentQueries() {
    const [recentQueries, setRecentQueries] = useState<string[]>([]);

    useEffect(() => {
        setRecentQueries(loadFromStorage());
    }, []);

    useEffect(() => {
        const onStorageChange = (e: StorageEvent) => {
            if (e.key !== STORAGE_KEY) return;
            try {
                setRecentQueries(e.newValue ? JSON.parse(e.newValue) : []);
            } catch {
                setRecentQueries([]);
            }
        };
        window.addEventListener('storage', onStorageChange);
        return () => window.removeEventListener('storage', onStorageChange);
    }, []);

    const addRecentQuery = useCallback((query: string) => {
        const trimmedQuery = query.trim();
        if (!trimmedQuery) return;

        setRecentQueries((prev) => {
            const filtered = prev.filter(
                (s) => s.toLowerCase() !== trimmedQuery.toLowerCase(),
            );
            const updated = [trimmedQuery, ...filtered].slice(
                0,
                MAX_RECENT_QUERIES,
            );
            persistToStorage(updated);
            return updated;
        });
    }, []);

    const removeRecentQuery = useCallback((query: string) => {
        setRecentQueries((prev) => {
            const updated = prev.filter(
                (s) => s.toLowerCase() !== query.toLowerCase(),
            );
            persistToStorage(updated);
            return updated;
        });
    }, []);

    const clearRecentQueries = useCallback(() => {
        setRecentQueries([]);
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch {}
    }, []);

    return {
        recentQueries,
        addRecentQuery,
        removeRecentQuery,
        clearRecentQueries,
    };
}
